/**
 * @file i18n : t(cle, params) type, changement de langue a chaud, detection initiale, formatage localise des nombres/durees/noms data-driven.
 */
import type { Locale } from '@shared/enums';
import type { LocalizedName } from '@config/types';
import { EN } from './en';
import type { TranslationKey } from './en';
import { FR } from './fr';

export type { TranslationKey };

const DICTS: Record<Locale, Record<TranslationKey, string>> = { en: EN, fr: FR };

export type TParams = Record<string, string | number>;

export class I18n {
  private listeners = new Set<(l: Locale) => void>();

  constructor(private current: Locale) {}

  get locale(): Locale {
    return this.current;
  }

  setLocale(l: Locale): void {
    if (l === this.current) return;
    this.current = l;
    document.documentElement.lang = l;
    for (const fn of this.listeners) fn(l);
  }

  onChange(fn: (l: Locale) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Traduction avec interpolation `{nom}`. Une cle absente renvoie la cle (visible en dev). */
  t(key: TranslationKey, params?: TParams): string {
    const raw = DICTS[this.current][key] ?? EN[key] ?? key;
    return params ? raw.replace(/\{(\w+)\}/g, (_, k: string) => String(params[k] ?? `{${k}}`)) : raw;
  }

  /** Cle dynamique (ex. `mode.${id}.title`, `error.${code}`) : verification a l'execution. */
  tDyn(key: string, params?: TParams): string {
    return key in EN ? this.t(key as TranslationKey, params) : key;
  }

  /** Nom localise des contenus data-driven (armes, skins, cibles...). */
  name(n: LocalizedName): string {
    return n[this.current];
  }
}

/** Langue initiale : reglage sauvegarde, sinon langue du navigateur (fr si "fr*"), sinon en. */
export const detectLocale = (saved?: Locale): Locale => {
  if (saved) return saved;
  const nav = typeof navigator !== 'undefined' ? navigator.language : 'en';
  return nav.toLowerCase().startsWith('fr') ? 'fr' : 'en';
};
